import { useEffect, useState } from "react";
import { CustomerLayout } from "@/components/CustomerLayout";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ownerNav } from "@/config/nav";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Headset } from "@phosphor-icons/react";

const PRIOS = ["LOW", "MEDIUM", "HIGH", "URGENT"];

function Content() {
  const [tab, setTab] = useState("ticket");
  const [tickets, setTickets] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [tf, setTf] = useState({ subject: "", category: "General", priority: "MEDIUM", description: "", booking_id: "" });
  const [df, setDf] = useState({ booking_id: "", reason: "", description: "" });

  const load = () => {
    api.get("/support/tickets/my").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/disputes/my").then((r) => setDisputes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submitTicket = () => {
    if (!tf.subject || !tf.description) { toast.error("Subject and description are required"); return; }
    api.post("/support/tickets", tf).then(() => { toast.success("Ticket raised"); setTf({ subject: "", category: "General", priority: "MEDIUM", description: "", booking_id: "" }); load(); }).catch(() => toast.error("Failed"));
  };
  const submitDispute = () => {
    if (!df.booking_id || !df.reason) { toast.error("Booking ID and reason are required"); return; }
    api.post("/disputes", df).then(() => { toast.success("Dispute raised"); setDf({ booking_id: "", reason: "", description: "" }); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  };

  return (
    <div className="px-4 pt-5" data-testid="support-center">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-semibold text-stone-900"><Headset size={24} className="text-primary" /> Support Center</h1>
      <div className="mt-4 flex gap-2">
        <button data-testid="support-tab-ticket" onClick={() => setTab("ticket")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === "ticket" ? "bg-primary text-white" : "border border-stone-200 text-stone-600"}`}>Support Ticket</button>
        <button data-testid="support-tab-dispute" onClick={() => setTab("dispute")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === "dispute" ? "bg-primary text-white" : "border border-stone-200 text-stone-600"}`}>Raise Dispute</button>
      </div>

      {tab === "ticket" ? (
        <div className="mt-4 space-y-3 rounded-2xl border border-stone-200 bg-white p-4">
          <Input data-testid="ticket-subject" placeholder="Subject" value={tf.subject} onChange={(e) => setTf({ ...tf, subject: e.target.value })} />
          <div className="flex gap-2">
            <Input data-testid="ticket-category" placeholder="Category" value={tf.category} onChange={(e) => setTf({ ...tf, category: e.target.value })} />
            <select data-testid="ticket-priority" value={tf.priority} onChange={(e) => setTf({ ...tf, priority: e.target.value })} className="rounded-lg border border-stone-200 px-3 text-sm">{PRIOS.map((p) => <option key={p}>{p}</option>)}</select>
          </div>
          <Input data-testid="ticket-booking" placeholder="Booking ID (optional)" value={tf.booking_id} onChange={(e) => setTf({ ...tf, booking_id: e.target.value })} />
          <Textarea data-testid="ticket-description" placeholder="Describe your issue" value={tf.description} onChange={(e) => setTf({ ...tf, description: e.target.value })} />
          <Button data-testid="ticket-submit" className="rounded-full" onClick={submitTicket}>Submit ticket</Button>
        </div>
      ) : (
        <div className="mt-4 space-y-3 rounded-2xl border border-stone-200 bg-white p-4">
          <Input data-testid="dispute-booking" placeholder="Booking ID" value={df.booking_id} onChange={(e) => setDf({ ...df, booking_id: e.target.value })} />
          <Input data-testid="dispute-reason" placeholder="Reason" value={df.reason} onChange={(e) => setDf({ ...df, reason: e.target.value })} />
          <Textarea data-testid="dispute-description" placeholder="Describe the dispute" value={df.description} onChange={(e) => setDf({ ...df, description: e.target.value })} />
          <Button data-testid="dispute-submit" className="rounded-full" onClick={submitDispute}>Submit dispute</Button>
        </div>
      )}

      <h2 className="mt-6 font-heading text-lg font-semibold text-stone-900">My Tickets</h2>
      <div className="mt-2 space-y-2" data-testid="my-tickets">
        {tickets.length === 0 ? <p className="text-sm text-stone-400">No tickets yet.</p> : tickets.map((t) => <div key={t.id} className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm"><span className="font-medium">{t.ticket_ref}</span> · {t.subject} <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-xs">{t.status}</span></div>)}
      </div>
      <h2 className="mt-6 font-heading text-lg font-semibold text-stone-900">My Disputes</h2>
      <div className="mt-2 space-y-2" data-testid="my-disputes">
        {disputes.length === 0 ? <p className="text-sm text-stone-400">No disputes yet.</p> : disputes.map((d) => <div key={d.id} className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm"><span className="font-medium">{d.dispute_ref}</span> · {d.booking_ref} <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-xs">{d.status}</span></div>)}
      </div>
    </div>
  );
}

export default function SupportCenter() {
  const { user } = useAuth();
  if (user?.role === "owner") return <DashboardLayout nav={ownerNav} title="Owner"><Content /></DashboardLayout>;
  return <CustomerLayout><Content /></CustomerLayout>;
}
